/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {
  useInfiniteQuery,
  useQuery
} from 'react-query';
import {
  loadAllDeviceFiles,
  loadDeviceFiles
} from '@ion/core/src/services/Connect';
import {DeviceFile, EntityStatus} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {
  createKey,
  QueryKey
} from '../config/QueryKeys';
import {
  createQueryResult,
  invalidateQueryCache
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceFilesResult = UseDefaultQueryResult & {
  deviceFiles: DeviceFile[];
  key: string[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
};

/**
 * The useDeviceFiles hook provides a React Query hook for referencing device files.
 *
 * ```typescript
 *
 * import {useDeviceFiles} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {deviceFiles} = useDeviceFiles();
 *
 * ```
 *
 * @param   accountId ID of the account within which to perform the device file search.
 * @param   platformIds Platform IDs (device types + file types) to use when performing
 *          the device file search.
 * @param   appIds App IDs to use when performing the device file search.
 * @param   includeSubAccounts Whether to search sub-accounts of the main account.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to Infinity.
 * @returns UseDeviceFilesResult containing the query results and the device files.
 */
export const useDeviceFiles = ({
  accountId,
  field,
  searchTerm,
  suppressNullVersions = false,
  enabledOnly = false,
  devicePlatformIds = [],
  platformIds = [],
  appIds = [],
  isRootAccount = false,
  includeSubAccounts = false,
  enabled = true,
  staleTime = Infinity
}): UseDeviceFilesResult => {
  const key = useMemo(() => {
    return createKey(
      QueryKey.DEVICE_FILES,
      accountId,
      devicePlatformIds,
      platformIds,
      appIds,
      field,
      searchTerm,
      includeSubAccounts
    );
  }, [
    accountId,
    field,
    searchTerm,
    safeJSONStringify(devicePlatformIds),
    safeJSONStringify(platformIds),
    safeJSONStringify(appIds),
    includeSubAccounts
  ]);

  const infiniteQuery = useInfiniteQuery(key, async({pageParam = 1}) => {
    return await loadDeviceFiles({
      pageId           : pageParam,
      pageSize         : 100,
      sortOn           : ['+validationMetaData.fields.version'],
      status           : enabledOnly ? EntityStatus.Enabled : undefined,
      filterOn         : createFilter({platformIds, appIds, devicePlatformIds, field, searchTerm}),
      includeSubGroups : includeSubAccounts,
      accountId,
      suppressNullVersions
    });
  }, {
    enabled,
    staleTime        : staleTime as number,
    getNextPageParam : (lastPage, allPages) => {
      const loadedSoFar = allPages?.length * 100;
      return lastPage?.totalCount > loadedSoFar ? allPages?.length + 1 : undefined;
    }
  });

  const deviceFiles = useMemo(() => {
    return infiniteQuery?.data?.pages?.flatMap(page => page?.items) || [];
  }, [infiniteQuery?.data]);

  return createQueryResult(infiniteQuery, {
    key,
    deviceFiles,
    isLoading          : infiniteQuery?.isLoading,
    hasNextPage        : infiniteQuery?.hasNextPage,
    fetchNextPage      : infiniteQuery?.fetchNextPage,
    isFetchingNextPage : infiniteQuery?.isFetchingNextPage
  }) as UseDeviceFilesResult;
};

export const invalidateDeviceFiles = (key = QueryKey.DEVICE_FILES) => {
  invalidateQueryCache(key);
};

export const invalidatePreCachedDeviceFiles = (key: string[]) => {
  invalidateQueryCache(key);
};

/**
 * Creates a search filter for fetching device files based on the specified platform ID
 * and app IDs (as well as always being valid).
 */
const createFilter = ({platformIds, appIds, devicePlatformIds, field, searchTerm}) => {
  const filter = [] as any;
  if (devicePlatformIds?.length && platformIds?.length) {
    devicePlatformIds.forEach(id => {
      platformIds.forEach(platformId => {
        filter.push({
          ...createVbusFilter(id, field, searchTerm),
          ...createPlatformIdFilter(platformId)
        }, {
          ...createPlatformIdFilter(parseInt(platformId)),
          ...createVbusFilter(id, field, searchTerm)
        });
      });
    });
  } else if (platformIds?.length && appIds?.length) {
    platformIds.forEach(platformId => {
      appIds.forEach(appId => {
        filter.push({
          ...createPlatformIdFilter(platformId),
          ...createAppIdFilter(appId, field, searchTerm)
        }, {
          ...createPlatformIdFilter(parseInt(platformId)),
          ...createAppIdFilter(appId, field, searchTerm)
        });
      });
    });
  } else if (platformIds?.length) {
    platformIds.forEach(platformId => {
      filter.push({
        ...createPlatformIdFilter(platformId),
        ...((field === 'version' && searchTerm !== '') &&
        createFieldFilter(field, searchTerm))
      }, {
        ...createPlatformIdFilter(parseInt(platformId)),
        ...((field === 'version' && searchTerm !== '') &&
        createFieldFilter(field, searchTerm))
      });
    });
  } else if (appIds?.length) {
    appIds.forEach(appId => {
      filter.push({
        ...createAppIdFilter(appId, field, searchTerm)
      });
    });
  };
  return {
    validationStatus : '{\"status\":\"VALID\"}',
    ...(filter.length && {validationMetaData: safeJSONStringify(filter)}),
    ...((field === 'name' && searchTerm !== '') && {name: `*${searchTerm}*`})
  };
};

const createPlatformIdFilter = (platformId: string | number) => ({platformId});

const createFieldFilter = (field, searchTerm) => ({
  fields : {
    [field] : `*${searchTerm}*`
  }
});

const createAppIdFilter = (appId: string, field, searchTerm) => ({
  fields : {
    appId : [{value: `${appId}`}],
    ...((field === 'version' && searchTerm !== '') &&
        createFieldFilter(field, searchTerm))?.fields
  }
});

const createVbusFilter = (platformId: string | number, field, searchTerm) => ({
  fields : {
    vBus : [{id: platformId}],
    ...((field === 'version' && searchTerm !== '') &&
        createFieldFilter(field, searchTerm))?.fields
  }
});

export default useDeviceFiles;
